import React from 'react';
import { Link } from 'react-router-dom';
import { Send, ShieldAlert, Instagram, Mail } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

function isMarketOpen() {
  const now = new Date();
  const ny = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const day = ny.getDay();
  if (day === 0 || day === 6) return false;
  const mins = ny.getHours() * 60 + ny.getMinutes();
  return mins >= 570 && mins < 960;
}

export default function Footer() {
  const { t } = useLang();
  const open = isMarketOpen();

  return (
    <footer className="bg-[#0B1220] border-t border-slate-800" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="mb-4">
              <span className="font-display text-lg bg-gradient-to-r from-blue-400 to-green-400 bg-clip-text text-transparent">ALEFTRADERS</span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs">{t('footer.tagline')}</p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-slate-700 px-3 py-1.5" data-testid="market-status-badge">
              <span className={`w-1.5 h-1.5 rounded-full ${open ? 'bg-green-400 pulse-dot' : 'bg-rose-500'}`} />
              <span className="text-[11px] font-semibold tracking-wide text-slate-300">
                NYSE · {open ? t('footer.marketOpen') : t('footer.marketClosed')}
              </span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold tracking-widest text-slate-500 mb-4">{t('footer.links')}</p>
            <div className="flex flex-col gap-2.5">
              <Link data-testid="footer-link-home" to="/" className="text-sm text-slate-400 hover:text-blue-300 transition-colors">{t('nav.home')}</Link>
              <Link data-testid="footer-link-selection" to="/selection" className="text-sm text-slate-400 hover:text-blue-300 transition-colors">{t('nav.selection')}</Link>
              <Link data-testid="footer-link-training" to="/training" className="text-sm text-slate-400 hover:text-blue-300 transition-colors">{t('nav.training')}</Link>
            </div>
          </div>
          <div className="flex flex-col items-start gap-3">
            <a
              data-testid="footer-telegram-btn"
              href="https://t.me/AlefTradersSupport"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-blue-500/40 px-5 py-3 text-sm font-medium text-blue-300 transition-all duration-300 hover:bg-blue-500/10 hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]"
            >
              <Send className="w-4 h-4" />
              {t('footer.tgBtn')}
            </a>
            <a
              data-testid="footer-instagram-btn"
              href="https://instagram.com/aleftraders"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-fuchsia-400/40 px-5 py-3 text-sm font-medium text-fuchsia-300 transition-all duration-300 hover:bg-fuchsia-400/10 hover:shadow-[0_0_20px_rgba(232,121,249,0.25)]"
            >
              <Instagram className="w-4 h-4" />
              Instagram · @aleftraders
            </a>
            <a
              data-testid="footer-email-link"
              href="mailto:info@aleftraders.com"
              className="inline-flex items-center gap-2 px-1 text-sm text-slate-400 hover:text-blue-300 transition-colors"
            >
              <Mail className="w-4 h-4" />
              info@aleftraders.com
            </a>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-slate-800/60">
          <p className="flex items-start gap-2 text-xs text-slate-500 leading-relaxed max-w-4xl" data-testid="risk-disclosure">
            <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-amber-500/70" />
            {t('footer.risk')}
          </p>
          <p className="mt-4 text-[11px] font-medium text-slate-600">{t('footer.rights')}</p>
        </div>
      </div>
    </footer>
  );
}
